import React, { useEffect, useRef, useState } from 'react';
import { Input } from './ui/input';
import { MapPin, Loader2, Check } from 'lucide-react';

const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const SCRIPT_ID = 'gmaps-places-script';

let mapsLoadPromise = null;
const loadGoogleMaps = () => {
  if (typeof window === 'undefined') return Promise.resolve(false);
  if (window.google?.maps?.places) return Promise.resolve(true);
  if (mapsLoadPromise) return mapsLoadPromise;
  if (!GOOGLE_MAPS_KEY) return Promise.resolve(false);

  mapsLoadPromise = new Promise((resolve) => {
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', () => resolve(true));
      existing.addEventListener('error', () => resolve(false));
      return;
    }
    // v=weekly + loading=async + libraries=places gives access to both legacy
    // and new (PlaceAutocompleteElement, AutocompleteSuggestion) APIs.
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_KEY}&v=weekly&libraries=places&loading=async`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
  return mapsLoadPromise;
};

// Pre-load the Maps script as soon as this module is imported so the API is
// ready by the time the user opens the intake-form modal & starts typing.
if (typeof window !== 'undefined' && GOOGLE_MAPS_KEY) {
  loadGoogleMaps();
}

/**
 * PlaceAutocompleteInput — uses the modern AutocompleteSuggestion API
 * (replaces the deprecated AutocompleteService). Renders our own dropdown
 * styled to match the form (so it looks identical to the rest of shadcn).
 *
 * Falls back to a plain text input if Maps cannot load — value still flows
 * via onChange so manual entry continues to work.
 */
const PlaceAutocompleteInput = ({
  value,
  onChange,
  onPlaceSelected,
  placeholder,
  language,
  testId = 'input-pob',
}) => {
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasPicked, setHasPicked] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);

  // Load script once
  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps().then((ok) => {
      if (cancelled) return;
      setIsLoading(false);
      setIsReady(!!(ok && window.google?.maps?.places));
    });
    return () => { cancelled = true; };
  }, []);

  // Once Google Maps becomes ready, if the user already typed something,
  // fetch suggestions for the existing query (avoids the race where users
  // type faster than the script can load).
  useEffect(() => {
    if (isReady && value && value.length >= 2 && !hasPicked) {
      fetchSuggestions(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady]);

  // Click outside to close dropdown
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSug(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchSuggestions = async (query) => {
    if (!isReady || !query || query.length < 2) {
      setSuggestions([]);
      return;
    }
    const places = window.google.maps.places;
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new places.AutocompleteSessionToken();
    }

    // 1) Try new AutocompleteSuggestion API (post-March 2025)
    if (places.AutocompleteSuggestion?.fetchAutocompleteSuggestions) {
      try {
        const { suggestions: sug } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: query,
          includedPrimaryTypes: ['(cities)'],
          sessionToken: sessionTokenRef.current,
        });
        const list = (sug || []).map((s) => {
          const p = s.placePrediction;
          return p ? {
            id: p.placeId,
            text: p.text?.toString() || '',
            mainText: p.mainText?.toString() || p.text?.toString() || '',
            secondaryText: p.secondaryText?.toString() || '',
            placePrediction: p,
          } : null;
        }).filter(Boolean);
        setSuggestions(list);
        setShowSug(list.length > 0);
        setActiveIdx(-1);
        return;
      } catch (err) {
        // Will fall through to legacy
        console.warn('New Places API failed, falling back to legacy:', err?.message || err);
      }
    }

    // 2) Fallback to legacy AutocompleteService
    if (places.AutocompleteService) {
      try {
        const svc = new places.AutocompleteService();
        svc.getPlacePredictions(
          { input: query, types: ['(cities)'], sessionToken: sessionTokenRef.current },
          (preds) => {
            const list = (preds || []).map((p) => ({
              id: p.place_id,
              text: p.description,
              mainText: p.structured_formatting?.main_text || p.description,
              secondaryText: p.structured_formatting?.secondary_text || '',
              legacyPlaceId: p.place_id,
            }));
            setSuggestions(list);
            setShowSug(list.length > 0);
            setActiveIdx(-1);
          }
        );
      } catch (err) {
        console.warn('Legacy Places API failed:', err);
        setSuggestions([]);
        setShowSug(false);
      }
    }
  };

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    if (hasPicked) setHasPicked(false);
    fetchSuggestions(v);
  };

  const handleSelect = async (sug) => {
    let lat = null;
    let lng = null;
    let label = sug.text;
    try {
      const places = window.google.maps.places;
      if (sug.placePrediction) {
        const place = sug.placePrediction.toPlace();
        await place.fetchFields({ fields: ['displayName', 'formattedAddress', 'location'] });
        lat = place.location?.lat();
        lng = place.location?.lng();
        label = place.formattedAddress || place.displayName || sug.text;
      } else if (sug.legacyPlaceId && places.PlacesService) {
        // legacy fallback
        const div = document.createElement('div');
        const svc = new places.PlacesService(div);
        await new Promise((res) => {
          svc.getDetails({ placeId: sug.legacyPlaceId, fields: ['geometry', 'formatted_address', 'name'] }, (place) => {
            if (place?.geometry?.location) {
              lat = place.geometry.location.lat();
              lng = place.geometry.location.lng();
              label = place.formatted_address || place.name || sug.text;
            }
            res();
          });
        });
      }
    } catch (err) {
      console.warn('Place details fetch failed:', err);
    }
    onChange(label);
    setSuggestions([]);
    setShowSug(false);
    setHasPicked(!!(lat && lng));
    sessionTokenRef.current = null; // session ends on selection
    if (onPlaceSelected && lat != null && lng != null) {
      onPlaceSelected({ place: label, latitude: lat, longitude: lng });
    }
  };

  const handleKeyDown = (e) => {
    if (!showSug || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (activeIdx >= 0) {
        e.preventDefault();
        handleSelect(suggestions[activeIdx]);
      }
    } else if (e.key === 'Escape') {
      setShowSug(false);
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-saffron/50 z-10 pointer-events-none" />
      <Input
        ref={inputRef}
        data-testid={testId}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => { if (suggestions.length > 0) setShowSug(true); }}
        placeholder={placeholder || (language === 'hi' ? 'दिल्ली, भारत' : 'Delhi, India')}
        autoComplete="off"
        className="border-saffron/30 focus:border-saffron focus:ring-saffron/20 h-12 pl-10 pr-10"
      />
      <div className="absolute right-3 top-1/2 -translate-y-1/2 z-10">
        {isLoading && <Loader2 className="w-4 h-4 text-saffron/40 animate-spin" />}
        {!isLoading && hasPicked && <Check className="w-4 h-4 text-green-600" data-testid="pob-verified" />}
      </div>

      {showSug && suggestions.length > 0 && (
        <ul
          data-testid="pob-suggestions"
          className="absolute z-50 left-0 right-0 mt-1 max-h-72 overflow-auto rounded-md border border-saffron/30 bg-white shadow-xl"
        >
          {suggestions.map((s, i) => (
            <li
              key={s.id}
              onMouseDown={(e) => { e.preventDefault(); handleSelect(s); }}
              onMouseEnter={() => setActiveIdx(i)}
              className={`px-3 py-2 cursor-pointer text-sm flex items-start gap-2 ${
                i === activeIdx ? 'bg-saffron/10' : 'hover:bg-saffron/5'
              }`}
            >
              <MapPin className="w-3.5 h-3.5 text-saffron mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-warm-text truncate">{s.mainText}</div>
                {s.secondaryText && (
                  <div className="text-xs text-warm-text-muted truncate">{s.secondaryText}</div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PlaceAutocompleteInput;
